import React, { useMemo, useState, useEffect } from "react";

/**
 * @component MoneyStreamlineChart
 * @description A high-fidelity SVG-based time-series chart optimized for financial data
 * across a standard 31-day cycle. Supports primary, comparison, and baseline datasets
 * with an interactive coordinate-tracking tooltip. Handles responsive corner-snapping on mobile.
 *
 * @param {Object} props - Component props.
 * @param {Object[]} props.currentMonthData - Primary dataset: Array of { day: number, amount: number }.
 * @param {Object[]} props.previousMonthData - Comparison dataset: Array of { day: number, amount: number }.
 * @param {Object[]|null} [props.averageData=[]] - Optional baseline dataset. Pass null to disable baseline rendering.
 * @param {number} [props.height=250] - Fixed height of the SVG viewport in pixels.
 */
const MoneyStreamlineChart = ({
  currentMonthData = [],
  previousMonthData = [],
  averageData = [],
  height = 250,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    setIsMobile(mediaQuery.matches);

    const handleMediaChange = (e) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener("change", handleMediaChange);
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  const width = 600;

  const padding = useMemo(() => {
    return isMobile
      ? { top: 15, right: 0, bottom: 0, left: 0 }
      : { top: 20, right: 30, bottom: 40, left: 55 };
  }, [isMobile]);

  const [hoverData, setHoverData] = useState(null);
  let averageInTooltip = true;

  if (averageData === null) {
    averageInTooltip = false;
    averageData = [];
  }

  const maxY = useMemo(() => {
    const allValues = [
      ...currentMonthData.map((d) => d.amount),
      ...previousMonthData.map((d) => d.amount),
      ...averageData.map((d) => d.amount),
    ];
    const highest = Math.max(...allValues, 10);

    if (isMobile) {
      return Math.ceil(highest * 1.15);
    }

    return Math.ceil(highest / 1000) * 1000;
  }, [currentMonthData, previousMonthData, averageData, isMobile]);

  const getX = (day) =>
    (day / 31) * (width - padding.left - padding.right) + padding.left;
  const getY = (amount) => {
    if (maxY === 0) return height - padding.bottom;
    return (
      height -
      (amount / maxY) * (height - padding.top - padding.bottom) -
      padding.bottom
    );
  };

  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) * width) / rect.width;

    const chartWidth = width - padding.left - padding.right;
    const day = Math.round(((mouseX - padding.left) / chartWidth) * 31);

    if (day >= 0 && day <= 31) {
      const current =
        currentMonthData.find((d) => d.day === day)?.amount ?? null;
      const prev = previousMonthData.find((d) => d.day === day)?.amount ?? null;
      const avg = averageData.find((d) => d.day === day)?.amount ?? null;

      setHoverData({ day, current, prev, avg, x: getX(day) });
    }
  };

  const generatePath = (data) => {
    if (!data.length) return "";
    return (
      `M ${getX(0)} ${getY(0)} ` +
      data.map((d) => `L ${getX(d.day)} ${getY(d.amount)}`).join(" ")
    );
  };

  const currentPath = useMemo(
    () => generatePath(currentMonthData),
    [currentMonthData, maxY, padding],
  );
  const previousPath = useMemo(
    () => generatePath(previousMonthData),
    [previousMonthData, maxY, padding],
  );
  const averagePath = useMemo(
    () => generatePath(averageData),
    [averageData, maxY, padding],
  );

  const yAxisTicks = useMemo(() => {
    if (isMobile) return [0, maxY * 0.5, maxY];
    return [0, maxY * 0.25, maxY * 0.5, maxY * 0.75, maxY];
  }, [maxY, isMobile]);

  const xAxisDays = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31];

  // NATIVE AUTOMATIC MONTH DETECTOR: Uses the current live browser time object
  // to get a 3-letter month abbreviation (e.g., "Jun"), no props required.
  const automaticMonthPrefix = useMemo(() => {
    return new Date().toLocaleString("en-US", { month: "short" });
  }, []);

  return (
    <div className="current-spend-chart-container">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverData(null)}
      >
        <defs>
          <linearGradient id="spendGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid Lines & Labels */}
        {yAxisTicks.map((tick, i) => (
          <React.Fragment key={i}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={getY(tick)}
              y2={getY(tick)}
              stroke="#F3F4F6"
              strokeWidth="1"
            />
            <text
              x={padding.left - 8}
              y={getY(tick)}
              textAnchor="end"
              alignmentBaseline="middle"
            >
              {tick === 0 ? "$0" : `$${(tick / 1000).toFixed(1)}k`}
            </text>
          </React.Fragment>
        ))}

        {xAxisDays.map((day, i) => (
          <text
            key={i}
            x={getX(day)}
            y={height - 10}
            textAnchor="middle"
            className="chart-x-axis-text"
          >
            {day}
          </text>
        ))}

        {/* Lines */}
        <path d={averagePath} fill="none" stroke="#E5E7EB" strokeWidth="2" />
        <path
          d={previousPath}
          fill="none"
          stroke="#F5A623"
          strokeWidth="2"
          strokeDasharray="4 4"
        />
        <path
          d={`${currentPath} L ${getX(
            currentMonthData[currentMonthData.length - 1]?.day || 0,
          )} ${getY(0)} L ${getX(0)} ${getY(0)} Z`}
          fill="url(#spendGradient)"
        />
        <path d={currentPath} fill="none" stroke="#3B82F6" strokeWidth="2.5" />

        {/* Hover Elements */}
        {hoverData && (
          <>
            <line
              x1={hoverData.x}
              x2={hoverData.x}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#3B82F6"
              strokeWidth="1"
              strokeDasharray="4 4"
            />

            {hoverData.avg !== null && (
              <circle
                cx={hoverData.x}
                cy={getY(hoverData.avg)}
                r="4"
                fill="white"
                stroke="#E5E7EB"
                strokeWidth="2"
              />
            )}
            {hoverData.prev !== null && (
              <circle
                cx={hoverData.x}
                cy={getY(hoverData.prev)}
                r="4"
                fill="white"
                stroke="#F5A623"
                strokeWidth="2"
              />
            )}
            {hoverData.current !== null && (
              <circle
                cx={hoverData.x}
                cy={getY(hoverData.current)}
                r="4"
                fill="white"
                stroke="#3B82F6"
                strokeWidth="2"
              />
            )}
          </>
        )}
      </svg>

      {/* Tooltip Card Overlay */}
      {hoverData && (
        <div
          className="chart-tooltip"
          style={{
            left: `${(hoverData.x / width) * 100}%`,
            top: `${(padding.top / height) * 100 + 5}%`,
          }}
        >
          {/* Displays dynamic calendar date cleanly (e.g. "Jun 9") */}
          <div className="tooltip-header">
            {automaticMonthPrefix} {hoverData.day}
          </div>

          <div className="tooltip-metric-row">
            <div className="tooltip-label-group">
              <div className="tooltip-dot-indicator bg-current" />
              <span className="tooltip-label-text">Current</span>
            </div>
            <span className="tooltip-value-text bold-metric">
              ${hoverData.current?.toLocaleString() || "--"}
            </span>
          </div>

          <div
            className={`tooltip-metric-row ${
              !averageInTooltip ? "last-row" : ""
            }`}
          >
            <div className="tooltip-label-group">
              <div className="tooltip-dot-indicator bg-previous" />
              <span className="tooltip-label-text">Previous</span>
            </div>
            <span className="tooltip-value-text semi-bold-metric">
              ${hoverData.prev?.toLocaleString() || "--"}
            </span>
          </div>

          {averageInTooltip && (
            <div className="tooltip-metric-row last-row">
              <div className="tooltip-label-group">
                <div className="tooltip-dot-indicator bg-average" />
                <span className="tooltip-label-text">Average</span>
              </div>
              <span className="tooltip-value-text semi-bold-metric">
                ${hoverData.avg?.toLocaleString() || "--"}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MoneyStreamlineChart;
